import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { DateAdapter, provideNativeDateAdapter } from '@angular/material/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs';
import { AlertComponent } from '@components/alert/alert.component';
import { ButtonComponent } from '@components/button/button.component';
import { ApiService, UserInfo } from 'app/services/api.service';
import { OnlineVisitCooldownDTO, OnlineVisitRequestDTO } from 'app/interfaces/online-visit.interface';

interface OnlineVisitForm {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    materials: Record<string, boolean>;
    spaces: Record<string, boolean>;
    selectedDate: Date | null;
    timeSlots: Record<string, boolean>;
    comments: string;
}

@Component({
    selector: 'app-online-visit',
    standalone: true,
    templateUrl: './online-visit.component.html',
    styleUrl: './online-visit.component.scss',
    imports: [
        CommonModule,
        FormsModule,
        TranslateModule,
        AlertComponent,
        ButtonComponent,
        MatDatepickerModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule
    ],
    providers: [provideNativeDateAdapter()]
})
export class OnlineVisitComponent implements OnInit {
    private readonly linkVideoEN = 'https://player.vimeo.com/video/1016097545?h=7170eefe36&title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479%27';
    private readonly linkVideoES = 'https://player.vimeo.com/video/1016097644?h=92e5bcf8d9&title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479';
    linkVideo: SafeResourceUrl | null = null;
    videoPlaying = false;

    loading = true;
    submitting = false;
    userInfo: UserInfo | null = null;
    cooldown: OnlineVisitCooldownDTO | null = null;
    successMessageVisible = false;
    errorMessageVisible = false;
    datePlaceholder = 'dd/mm/yyyy';

    minDate: Date = new Date();

    /** Festivos fijos nacionales (España) + Andalucía + Almería — mes 0-indexed */
    private readonly fixedHolidays: { month: number; day: number }[] = [
        { month: 0, day: 1 },   // Año Nuevo
        { month: 0, day: 6 },   // Epifanía del Señor
        { month: 1, day: 28 },  // Día de Andalucía
        { month: 4, day: 1 },   // Fiesta del Trabajo
        { month: 5, day: 24 },  // San Juan Bautista
        { month: 7, day: 15 },  // Asunción de la Virgen
        { month: 7, day: 29 },  // Virgen del Mar (Almería)
        { month: 9, day: 12 },  // Fiesta Nacional de España
        { month: 10, day: 1 },  // Todos los Santos
        { month: 11, day: 6 },  // Día de la Constitución
        { month: 11, day: 8 },  // Inmaculada Concepción
        { month: 11, day: 25 }, // Navidad
    ];

    /** Calcula la fecha de Domingo de Pascua  */
    private getEasterSunday(year: number): Date {
        const a = year % 19;
        const b = Math.floor(year / 100);
        const c = year % 100;
        const d = Math.floor(b / 4);
        const e = b % 4;
        const f = Math.floor((b + 8) / 25);
        const g = Math.floor((b - f + 1) / 3);
        const h = (19 * a + b - d - g + 15) % 30;
        const i = Math.floor(c / 4);
        const k = c % 4;
        const l = (32 + 2 * e + 2 * i - h - k) % 7;
        const m = Math.floor((a + 11 * h + 22 * l) / 451);
        const month = Math.floor((h + l - 7 * m + 114) / 31) - 1;
        const day = ((h + l - 7 * m + 114) % 31) + 1;
        return new Date(year, month, day);
    }

    private holidayCache = new Map<number, Set<string>>();

    private getHolidaysForYear(year: number): Set<string> {
        if (this.holidayCache.has(year)) {
            return this.holidayCache.get(year)!;
        }
        const set = new Set<string>();
        const addDate = (d: Date) => set.add(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);

        // Festivos fijos — si cae en domingo se traslada al lunes
        for (const h of this.fixedHolidays) {
            const d = new Date(year, h.month, h.day);
            if (d.getDay() === 0) {
                d.setDate(d.getDate() + 1);
            }
            addDate(d);
        }

        // Semana Santa: Jueves Santo y Viernes Santo
        const easter = this.getEasterSunday(year);
        const jueves = new Date(easter);
        jueves.setDate(easter.getDate() - 3);
        const viernes = new Date(easter);
        viernes.setDate(easter.getDate() - 2);
        addDate(jueves);
        addDate(viernes);

        this.holidayCache.set(year, set);
        return set;
    }

    dateFilter = (date: Date | null): boolean => {
        if (!date) return false;
        const dow = date.getDay();
        if (dow === 0 || dow === 6) return false;
        const key = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
        return !this.getHolidaysForYear(date.getFullYear()).has(key);
    };

    form: OnlineVisitForm = {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        materials: {
            quartzite: false,
            granite: false,
            marble: false,
            others: false,
        },
        spaces: {
            kitchen: false,
            bathroom: false,
            exterior: false,
            otherInteriors: false,
        },
        selectedDate: null,
        timeSlots: {
            morning: false,
            midday: false,
            afternoon: false,
        },
        comments: '',
    };

    materialsList = [
        { key: 'quartzite', labelKey: 'online_visit.material_quartzite', sfValue: 'Quartzite' },
        { key: 'granite', labelKey: 'online_visit.material_granite', sfValue: 'Granite' },
        { key: 'marble', labelKey: 'online_visit.material_marble', sfValue: 'Marble' },
        { key: 'others', labelKey: 'online_visit.material_others', sfValue: 'Others' },
    ];

    spacesList = [
        { key: 'kitchen', labelKey: 'online_visit.space_kitchen', sfValue: 'Kitchen' },
        { key: 'bathroom', labelKey: 'online_visit.space_bathroom', sfValue: 'Bathroom' },
        { key: 'exterior', labelKey: 'online_visit.space_exterior', sfValue: 'Exterior' },
        { key: 'otherInteriors', labelKey: 'online_visit.space_other_interior', sfValue: 'Other Indoor' },
    ];

    timeSlotsList = [
        { key: 'morning', labelKey: 'online_visit.time_morning', sfValue: 'Morning: from 9:00 to 12:00' },
        { key: 'midday', labelKey: 'online_visit.time_midday', sfValue: 'Noon: from 12:00 to 15:00' },
        { key: 'afternoon', labelKey: 'online_visit.time_afternoon', sfValue: 'Afternoon: from 15:00 to 18:00' },
    ];

    instructions = [
        { titleKey: 'online_visit.step1_title', descriptionKey: 'online_visit.step1_description' },
        { titleKey: 'online_visit.step2_title', descriptionKey: 'online_visit.step2_description' },
        { titleKey: 'online_visit.step3_title', descriptionKey: 'online_visit.step3_description' },
    ];

    private pendingLoads = 2;

    constructor(
        private api: ApiService,
        private sanitizer: DomSanitizer,
        private translate: TranslateService,
        private dateAdapter: DateAdapter<Date>
    ) {}

    ngOnInit() {
        this.translate.onLangChange.subscribe((value) => {
            this.updateMatLang(value.lang);
        });
        this.updateMatLang(this.translate.currentLang || undefined);

        this.resolveVideoUrl(null);
        this.loadUserInfo();
        this.loadCooldown();
    }

    private updateMatLang(lang?: string) {
        const materialLocale = this.resolveMaterialLocale(lang);
        this.dateAdapter.setLocale(materialLocale);
        this.datePlaceholder = this.getDatePlaceholderForLocale(materialLocale);
    }

    private resolveMaterialLocale(lang?: string): string {
        const raw = (
            lang ||
            this.translate.currentLang ||
            this.translate.getDefaultLang() ||
            this.userInfo?.language ||
            'en'
        )
            .replace('_', '-')
            .toLowerCase();

        // Business rule: any English variant must use US date format in Online Visit.
        if (raw.startsWith('en')) return 'en-US';
        if (raw === 'es') return 'es-ES';

        return raw;
    }

    private getDatePlaceholderForLocale(locale: string): string {
        try {
            const parts = new Intl.DateTimeFormat(locale, {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
            }).formatToParts(new Date(2001, 10, 22));

            const placeholder = parts
                .map((part) => {
                    if (part.type === 'day') return 'dd';
                    if (part.type === 'month') return 'mm';
                    if (part.type === 'year') return 'yyyy';
                    return part.value;
                })
                .join('');

            return placeholder || 'dd/mm/yyyy';
        } catch {
            return locale.startsWith('en') ? 'mm/dd/yyyy' : 'dd/mm/yyyy';
        }
    }

    get isBlocked(): boolean {
        return this.cooldown?.isBlocked ?? false;
    }

    toggleMaterial(key: string) {
        if (!this.canEdit()) {
            return;
        }
        this.form.materials[key] = !this.form.materials[key];
    }

    toggleSpace(key: string) {
        if (!this.canEdit()) {
            return;
        }
        this.form.spaces[key] = !this.form.spaces[key];
    }

    toggleTimeSlot(key: string) {
        if (!this.canEdit()) {
            return;
        }
        this.form.timeSlots[key] = !this.form.timeSlots[key];
    }

    isFormValid(): boolean {
        const hasSelectedMaterial = Object.values(this.form.materials).some(Boolean);
        const hasSelectedSpace = Object.values(this.form.spaces).some(Boolean);
        const hasSelectedTimeSlot = Object.values(this.form.timeSlots).some(Boolean);
        const hasSelectedDate = this.form.selectedDate !== null;
        const hasPhone = this.form.phone.trim().length > 0;

        return hasSelectedMaterial && hasSelectedSpace && hasSelectedTimeSlot && hasSelectedDate && hasPhone;
    }

    submitForm() {
        if (this.submitting || !this.isFormValid() || this.isBlocked) {
            return;
        }

        this.submitting = true;
        this.successMessageVisible = false;
        this.errorMessageVisible = false;

        const payload: OnlineVisitRequestDTO = {
            firstName: this.form.firstName,
            lastName: this.form.lastName,
            email: this.form.email,
            phone: this.form.phone,
            materials: this.extractSfValues(this.form.materials, this.materialsList),
            spaces: this.extractSfValues(this.form.spaces, this.spacesList),
            dates: this.form.selectedDate ? [this.formatDateForApi(this.form.selectedDate)] : [],
            timeSlots: this.extractSfValues(this.form.timeSlots, this.timeSlotsList),
            comments: this.form.comments,
        };

        this.api.createOnlineVisit(payload).pipe(take(1)).subscribe({
            next: () => {
                this.submitting = false;
                this.successMessageVisible = true;
                this.loadCooldown();
            },
            error: () => {
                this.submitting = false;
                this.errorMessageVisible = true;
            }
        });
    }

    private loadUserInfo() {
        this.api.userInfo.pipe(take(1)).subscribe((userInfo) => {
            this.userInfo = userInfo;
            this.updateMatLang(this.translate.currentLang || userInfo?.language || undefined);
            this.form.firstName = userInfo?.firstName || '';
            this.form.lastName = userInfo?.lastName || '';
            this.form.email = userInfo?.email || '';
            this.form.phone = userInfo?.phone || userInfo?.accountPhone || '';
            this.resolveVideoUrl(userInfo?.language);
            this.markLoaded();
        });
    }

    private loadCooldown() {
        this.api.getOnlineVisitCooldown().pipe(take(1)).subscribe({
            next: (cooldown) => {
                this.cooldown = cooldown;
                this.markLoaded();
            },
            error: () => {
                this.markLoaded();
            }
        });
    }

    private markLoaded() {
        this.pendingLoads -= 1;
        this.loading = this.pendingLoads > 0;
    }

    private canEdit(): boolean {
        return !this.isBlocked && !this.submitting;
    }

    private resolveVideoUrl(language: string | null | undefined) {
        const isSpanish = language?.toLowerCase().startsWith('es');
        const baseUrl = isSpanish ? this.linkVideoES : this.linkVideoEN;
        this.linkVideo = this.sanitizer.bypassSecurityTrustResourceUrl(baseUrl + '&autoplay=1');
    }

    playVideo() {
        this.videoPlaying = true;
    }

    private extractSelected(values: Record<string, boolean>): string[] {
        return Object.keys(values).filter((key) => values[key]);
    }

    private formatDateForApi(date: Date): string {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    private extractSfValues(
        values: Record<string, boolean>,
        list: { key: string; sfValue: string }[]
    ): string[] {
        return Object.keys(values)
            .filter((key) => values[key])
            .map((key) => {
                const item = list.find((i) => i.key === key);
                return item ? item.sfValue : key;
            });
    }
}
